import {
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Eye,
  MapPin,
  MoreVertical,
  Play,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { Meeting, MeetingStatus } from '../../types';
import { formatDate, formatTime } from '../../utils/dateUtils';

interface MeetingListItemProps {
  meeting: Meeting;
  onEdit: (meeting: Meeting) => void;
  onView: (meeting: Meeting) => void;
  onDelete: (meeting: Meeting) => void;
  showActions?: boolean;
}

const MeetingListItem: React.FC<MeetingListItemProps> = ({
  meeting,
  onEdit,
  onView,
  onDelete,
  showActions = true,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusIcon = (status: MeetingStatus) => {
    switch (status) {
      case 'scheduled':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'in_progress':
        return <Play className="h-4 w-4 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Calendar className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: MeetingStatus) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatMeetingTime = () => {
    const start = new Date(meeting.startDateTime);
    const end = new Date(meeting.endDateTime);
    const now = new Date();

    const isToday = start.toDateString() === now.toDateString();
    const isTomorrow =
      start.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();

    let dateStr = formatDate(start);
    if (isToday) {
      dateStr = 'Today';
    } else if (isTomorrow) {
      dateStr = 'Tomorrow';
    }

    return `${dateStr} • ${formatTime(start)} - ${formatTime(end)}`;
  };

  const getTotalInvitees = () => {
    return (meeting.projectMembers?.length || 0) + (meeting.externalUsers?.length || 0);
  };

  const isUpcoming = () => {
    return new Date(meeting.startDateTime) > new Date();
  };

  const isOngoing = () => {
    const now = new Date();
    return new Date(meeting.startDateTime) <= now && new Date(meeting.endDateTime) > now;
  };

  return (
    <div className="bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Left side - Main content */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Status Icon */}
            <div className="flex-shrink-0">{getStatusIcon(meeting.status)}</div>

            {/* Meeting Title */}
            <div className="flex-1 min-w-0">
              <h3
                className="font-medium text-gray-900 cursor-pointer hover:text-brand-primary transition-colors truncate"
                onClick={() => onView(meeting)}
              >
                {meeting.title}
              </h3>
            </div>

            {/* Status Badges - Compact */}
            <div className="flex items-center space-x-2">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(meeting.status)}`}
              >
                {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1).replace('_', ' ')}
              </span>

              {isOngoing() && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
                  <span className="text-xs font-medium text-green-700">Live</span>
                </div>
              )}
            </div>
          </div>

          {/* Center - Meeting metadata in horizontal layout */}
          <div className="hidden md:flex items-center space-x-4 text-sm text-gray-500 mx-6">
            {/* Date/Time */}
            <div className="flex items-center space-x-1">
              <Clock className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap">{formatMeetingTime()}</span>
            </div>

            {/* Location */}
            {meeting.location && (
              <div className="flex items-center space-x-1">
                <MapPin className="h-3.5 w-3.5" />
                <span className="truncate max-w-32">{meeting.location}</span>
              </div>
            )}

            {/* Invitees */}
            <div className="flex items-center space-x-1">
              <Users className="h-3.5 w-3.5" />
              <span>{getTotalInvitees()}</span>
            </div>

            {/* Attachments */}
            {meeting.attachments?.length > 0 && (
              <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                {meeting.attachments.length}
              </span>
            )}

            {/* Visibility */}
            <span
              className={`px-1.5 py-0.5 rounded text-xs ${
                meeting.visibility === 'private'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {meeting.visibility === 'private' ? 'Private' : 'Public'}
            </span>
          </div>

          {/* Right side - Actions */}
          {showActions && (
            <div className="flex items-center space-x-1 ml-2">
              {/* Quick actions */}
              <button
                onClick={() => onView(meeting)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </button>

              {isUpcoming() && meeting.status !== 'cancelled' && (
                <button
                  onClick={() => onEdit(meeting)}
                  className="p-1.5 text-gray-400 hover:text-brand-primary hover:bg-brand-50 rounded transition-colors"
                  title="Edit meeting"
                >
                  <Edit className="h-4 w-4" />
                </button>
              )}

              {/* More actions menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-1 w-44 rounded shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          onView(meeting);
                          setShowMenu(false);
                        }}
                        className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </button>

                      {isUpcoming() && meeting.status !== 'cancelled' && (
                        <>
                          <button
                            onClick={() => {
                              onEdit(meeting);
                              setShowMenu(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </button>

                          <div className="border-t border-gray-100"></div>
                          <button
                            onClick={() => {
                              onDelete(meeting);
                              setShowMenu(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mobile metadata - Only shown on small screens */}
        <div className="md:hidden mt-2 flex items-center space-x-4 text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <Clock className="h-3 w-3" />
            <span>{formatMeetingTime()}</span>
          </div>
          {meeting.location && (
            <div className="flex items-center space-x-1">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{meeting.location}</span>
            </div>
          )}
          <div className="flex items-center space-x-1">
            <Users className="h-3 w-3" />
            <span>{getTotalInvitees()}</span>
          </div>
        </div>

        {/* Description - Only if exists and compact */}
        {meeting.details && (
          <div className="mt-2">
            <p className="text-xs text-gray-600 line-clamp-1">{meeting.details}</p>
          </div>
        )}

        {/* Cancellation notice - Compact */}
        {meeting.status === 'cancelled' && (
          <div className="mt-2 flex items-center px-2 py-1 bg-red-50 border border-red-200 rounded">
            <XCircle className="h-3 w-3 text-red-500 mr-1" />
            <span className="text-xs font-medium text-red-800">Cancelled</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingListItem;
